import { useRef } from "react";
import "./HelpModal.css";
import { useModalA11y } from "./useModalA11y";
import { track } from "../analytics/posthog";
import { useI18n } from "../i18n";

/** Persistent crisis-help modal — opened by the "Get Urgent Help" button. */
export function HelpModal({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalA11y(dialogRef, onClose);

  return (
    <div
      className="modal-overlay"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div ref={dialogRef} className="modal" role="dialog" aria-modal="true" aria-label="Get urgent help">
        <h2 className="modal__title">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          {t("help.title")}
        </h2>
        <p className="modal__intro">{t("help.intro")}</p>
        <div className="modal__lines">
          <a className="modal__line" href="tel:999" onClick={() => track("crisis_resource_clicked", { resource: "999", surface: "help_modal" })}>
            <strong>Call 999</strong>
            <span>{t("help.emergency")}</span>
          </a>
          {/* Mid-tier between 999 and 111 (LLR, 11 Aug): urgent mental health
              support without needing an emergency response. */}
          <a className="modal__line" href="tel:08088003302" onClick={() => track("crisis_resource_clicked", { resource: "mh_urgent_support", surface: "help_modal" })}>
            <strong>{t("help.urgentMh")}</strong>
            <span>{t("help.urgentMhNote")}</span>
          </a>
          <a className="modal__line" href="tel:111" onClick={() => track("crisis_resource_clicked", { resource: "nhs_111", surface: "help_modal" })}>
            <strong>Call NHS 111 (press 2)</strong>
            <span>{t("help.mentalHealth")}</span>
          </a>
          <a className="modal__line" href="tel:116123" onClick={() => track("crisis_resource_clicked", { resource: "samaritans", surface: "help_modal" })}>
            <strong>Call Samaritans on 116 123</strong>
            <span>Free, confidential, 24/7</span>
          </a>
          <a className="modal__line" href="sms:85258&body=SHOUT" onClick={() => track("crisis_resource_clicked", { resource: "shout_text", surface: "help_modal" })}>
            <strong>Text SHOUT to 85258</strong>
            <span>Free 24/7 crisis text line</span>
          </a>
        </div>
        <button type="button" className="modal__close" onClick={onClose}>
          {t("help.close")}
        </button>
      </div>
    </div>
  );
}
